// contexts/AuthContext.tsx
import React, { createContext, useContext, useEffect, useState, ReactNode, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import LogRocket from 'logrocket';
import { isTokenExpired, getTokenExpirationTime } from "../utils/auth";
import { clearAllStorageData } from "../utils/storage";

interface UserData {
  id: string;
  email: string;
  role: string;
  is_anonymous?: boolean;
  app_metadata?: any;
  user_metadata?: any;
}

interface AuthContextType {
  isAuthenticated: boolean;
  token: string | null;
  user: UserData | null;
  userId: string | null;
  isPaidUser: boolean;
  checkAuthStatus: () => boolean;
  handleLogout: () => void;
  getPaidUser: () => { userId: string; email: string; token: string } | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [token, setToken] = useState<string | null>(null);
  const [user, setUser] = useState<UserData | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  const [isPaidUser, setIsPaidUser] = useState(false);

  const navigate = useNavigate();

  // Memoize handleLogout to prevent unnecessary re-renders
  const handleLogout = useCallback(() => {
    console.log("🚪 Logging out user - clearing all localStorage data");
    
    // Clear all localStorage data using utility function
    clearAllStorageData();
    
    // Reset state
    setIsAuthenticated(false);
    setToken(null);
    setUser(null);
    setUserId(null);
    setIsPaidUser(false);
    
    // Navigate to login
    navigate("/login");
  }, [navigate]);

  // Memoize checkAuthStatus to prevent infinite loops
  const checkAuthStatus = useCallback((): boolean => {
    const storedToken = localStorage.getItem("access_token");
    const storedUser = localStorage.getItem("user_data");

    if (!storedToken || isTokenExpired(storedToken)) {
      // Only update state if it's different from current state
      if (isAuthenticated || token || user || userId || isPaidUser) {
        setIsAuthenticated(false);
        setToken(null);
        setUser(null);
        setUserId(null);
        setIsPaidUser(false);
      }
      return false;
    }

    // Only update state if it's different from current state
    if (!isAuthenticated) setIsAuthenticated(true);
    if (token !== storedToken) setToken(storedToken);

    if (storedUser) {
      try {
        const parsedUser: UserData = JSON.parse(storedUser);
        
        // Only update if user data has changed
        if (JSON.stringify(user) !== JSON.stringify(parsedUser)) {
          setUser(parsedUser);
          setUserId(parsedUser.id || null);
          setIsPaidUser(parsedUser.is_anonymous === false);
          const email = parsedUser.email || parsedUser.user_metadata?.email || "Unknown";
          // Identify user in LogRocket
          LogRocket.identify(parsedUser.id, {
            name: parsedUser.email?.split('@')[0] || 'Unknown User',
            email: email,
            role: parsedUser.role,
            subscriptionType: parsedUser.is_anonymous === false ? 'paid' : 'guest'
          });
        }
      } catch (error) {
        console.error("❌ Failed to parse user_data:", error);
      }
    }

    return true;
  }, [isAuthenticated, token, user, userId, isPaidUser]);

  // Memoize getPaidUser
  const getPaidUser = useCallback(() => {

    if (isAuthenticated && isPaidUser && user && token) {
      return {
        userId: user.id,
        email: user.email,
        token,
      };
    }

    console.warn("⚠️ getPaidUser: Not a paid user or missing data");
    return null;
  }, [isAuthenticated, isPaidUser, user, token]);

  // Memoize setupTokenExpirationCheck
  const setupTokenExpirationCheck = useCallback((tokenToCheck: string) => {
    const expirationTime = getTokenExpirationTime(tokenToCheck);
    if (expirationTime) {
      const timeUntilExpiration = expirationTime - Date.now();

      if (timeUntilExpiration > 0) {
        setTimeout(() => {
          console.log("⏰ Token expired, logging out user...");
          handleLogout();
        }, timeUntilExpiration);

        const warningTime = timeUntilExpiration - 5 * 60 * 1000;
        if (warningTime > 0) {
          setTimeout(() => {
            console.warn("⚠️ Token will expire in 5 minutes");
          }, warningTime);
        }
      }
    }
  }, [handleLogout]);

  // Initialize auth status on mount only
  useEffect(() => {
    checkAuthStatus();
  }, []); // Empty dependency array - only run on mount

  // Set up token expiration check when token changes
  useEffect(() => {
    if (token) {
      setupTokenExpirationCheck(token);
    }
  }, [token, setupTokenExpirationCheck]);

  // Set up periodic checks and storage event listener
  useEffect(() => {
    const interval = setInterval(() => {
      const currentToken = localStorage.getItem("access_token");
      if (currentToken && isTokenExpired(currentToken)) {
        console.log("🔄 Token expired during periodic check");
        handleLogout();
      }
    }, 60000);

    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === "access_token" && !e.newValue) {
        setIsAuthenticated(false);
        setToken(null);
        navigate("/login");
      }
    };

    window.addEventListener("storage", handleStorageChange);

    return () => {
      clearInterval(interval);
      window.removeEventListener("storage", handleStorageChange);
    };
  }, [handleLogout, navigate]);

  const value: AuthContextType = {
    isAuthenticated,
    token,
    user,
    userId,
    isPaidUser,
    checkAuthStatus,
    handleLogout,
    getPaidUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};